import React, { memo, useCallback, useEffect } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import { css, Global } from "@emotion/react";
import { isEmpty } from "lodash";

import { DEFAULT_GLOBAL_CURRENCY, DEFAULT_GLOBAL_CURRENCY_SYMBOL } from "core/config/config";
import { useAppState } from "core/contexts/AppContext";
import useBreakpoint from "core/hooks/useBreakpoint";
import useSetState from "core/hooks/useSetState";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";
import { getFooterMenu, getURLSegment, getURLSegments } from "core/includes/formatters";
import { getCanRenderWebp } from "core/includes/image";
import { persist, retrieveValue } from "core/includes/localStorage";
import { isIncludeInBreadcrumbTargets } from "core/includes/segments";
import { getFeatureFlags, getGlobalCurrency, pageMenuQuery } from "core/includes/queries";

import BreadcrumbContainer from "core/Components/Breadcrumb/BreadcrumbContainer";
import SectionContainer from "core/Components/SectionContainer";
import CartIcon from "core/Components/Navigations/CartIcon";
import NavContainer from "core/Components/Navigations/NavContainer";
import NavCountryDropdown from "core/Components/Navigations/NavCountryDropdown";
import NavLinkButtons from "core/Components/Navigations/NavLinkButtons";
import NavLinkContainer from "core/Components/Navigations/NavLinkContainer";
import NavLogo from "core/Components/Navigations/NavLogo";
import NavShadowBox from "core/Components/Navigations/NavShadowBox";
import NavSide from "core/Components/Navigations/NavSide";
import NavWrapper from "core/Components/Navigations/NavWrapper";

import BurgerNavContainer from "core/Components/HamburgerNavigations/BurgerNavContainer";
import BurgerNavHeaderContainer from "core/Components/HamburgerNavigations/BurgerNavHeaderContainer";
import BurgerNavMainContainer from "core/Components/HamburgerNavigations/BurgerNavMainContainer";
import BurgerNavMenuIcon from "core/Components/HamburgerNavigations/BurgerNavMenuIcon";
import BurgerNavWrapper from "core/Components/HamburgerNavigations/BurgerNavWrapper";

import BurgerNavHeaderMenuBox from "core/Components/HamburgerNavigations/large/BurgerNavHeaderMenuBox";
import BurgerNavLeft from "core/Components/HamburgerNavigations/large/BurgerNavLeft";
import BurgerNavRight from "core/Components/HamburgerNavigations/large/BurgerNavRight";

import BurgerNavSmallCartWrapper from "core/Components/HamburgerNavigations/small/BurgerNavSmallCartWrapper";
import BurgerNavSmallContainer from "core/Components/HamburgerNavigations/small/BurgerNavSmallContainer";
import BurgerNavSmallCountryDropdownContainer from "core/Components/HamburgerNavigations/small/BurgerNavSmallCountryDropdownContainer";
import BurgerNavSmallRightButtonsContainer from "core/Components/HamburgerNavigations/small/BurgerNavSmallRightButtonsContainer";

import useCart from "pages/Components/CartPage/hooks/useCart";
import ConfirmationModal from "pages/Components/CartAndCheckout/components/ConfirmationModal";
import { isDown, isOnly, isUp } from "theme/breakpoints";

type DefaultState = {
    selectedHeaderMenu?: StandardPage, // General: Selected header menu
    isClick: boolean, // Over 1280px only (Option to hide the submenu)
    showBurgerMenu: boolean, // Less than 1024px only (toggle)
    isBurgerMenuClick: boolean, // Less than 1280px (Option to set the underline & its colour between clicked menu & selected menu)
    // Common data (Header, Footer, Countries)
    pages?: [StandardMenu],
    headerData?: {
        headerMenuAll?: [StandardPage],
        footerMenuAll?: [StandardPage],
        menuButton?: Button, // Button next to Login button
        countries?: [CountryItem],
    }
    showConfirmationCartModal: boolean,
};

const INITIAL_STATE = {
    selectedHeaderMenu: undefined,
    isClick: false,
    showBurgerMenu: false,
    isBurgerMenuClick: false,
    pages: undefined,
    headerData: undefined,
    showConfirmationCartModal: false,
};

type Props = {
    stage: string | null,
    footerMenuHandler: (pages: any) => void,
    breadcrumbHandler: (showBreadcrumb: boolean) => void,
    hideHeaderCountry: boolean,
    isDisabledCartIcon: boolean,
};

const Header: React.FC<Props> = ({ stage, footerMenuHandler, breadcrumbHandler, hideHeaderCountry = false, isDisabledCartIcon = false }) => {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const location = useLocation();
    const currentURLSegment = getURLSegment(location.pathname);
    const urlSegments = getURLSegments(location.pathname);

    const [appState, setAppState] = useAppState();
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const showBreadcrumb = isIncludeInBreadcrumbTargets(currentURLSegment);
    const canRenderWebp = getCanRenderWebp();
    const { breakpoint } = useBreakpoint();
    const { getCart } = useCart();

    // Default set
    useEffect(() => {
        const selectedCountry = retrieveValue("selected-country")?.replace(/"/g, "") ?? "";

        setAppState((prev: {
            globalCurrencyOptions: GlobalCurrencyOptions,
            countryCode: {
                defaultCode: string,
                code: string,
            }
        }) => ({
            currentURLSegment,
            globalCurrencyOptions: {
                ...prev.globalCurrencyOptions,
                isGlobal: selectedCountry === "other",
            },
            countryCode: {
                ...prev.countryCode,
                defaultCode: prev.countryCode.defaultCode,
                code: prev.countryCode.code || selectedCountry
            },
        }));
    }, [currentURLSegment, setAppState]);

    const { data } = useQuery(pageMenuQuery, {
        variables: { stage, canRenderWebp },
        context: {
            headers: {
                'Selected-Country': appState.countryCode.code || (retrieveValue("selected-country")?.replace(/"/g, "") ?? "")
            }
        }
    });

    const { data: featureFlagsData, loading: featureFlagsLoading } = useQuery(getFeatureFlags);
    const { data: globalCurrencyData } = useQuery(getGlobalCurrency);

    // Re-fetch when country code is changed
    useEffect(() => {
        if (!state.pages ||
            (appState.countryCode.code && appState.countryCode.defaultCode !== appState.countryCode.code)
        ) {
            apolloClient.refetchQueries({ include: "active" });
        }
    }, [
        apolloClient,
        state.pages,
        appState.countryCode.defaultCode,
        appState.countryCode.code,
        canRenderWebp,
        stage,
    ]);

    // Get each data according to segment from common header data
    const getDataForEachSegment = useCallback((segment: string) => {
        const segmentData = data?.pageMenu?.find((p: StandardMenu) => p.segment === segment);

        switch (segment) {
            case "menuButton":
                return segmentData?.menuButton;
            case "accessed-country":
                return segmentData?.selectedCountry;
            case "countries":
                return segmentData?.countries;
            case "user-country":
                return segmentData?.userCountry;
            default:
                return segmentData?.standardPage;
        }
    }, [data?.pageMenu]);

    const getSelectedCountryData = useCallback((countryData: CountryCodeItem) => {
        const countries = getDataForEachSegment("countries");
        const accessedCountry = getDataForEachSegment('accessed-country');
        if (!countries.length || !accessedCountry || (accessedCountry === 'other' && !retrieveValue("selected-country"))) {
            if (countryData) {
                return {
                    defaultCode: DEFAULT_COUNTRY_CODE_CONFIG.code,
                    code: countryData.code ?? DEFAULT_COUNTRY_CODE_CONFIG.code,
                    name: countryData.name ?? DEFAULT_COUNTRY_CODE_CONFIG.name,
                    taxLabel: countryData.taxLabel ?? DEFAULT_COUNTRY_CODE_CONFIG.taxLabel,
                    currency: countryData.currency ?? DEFAULT_COUNTRY_CODE_CONFIG.currency,
                    taxRate: countryData.taxRate ?? DEFAULT_COUNTRY_CODE_CONFIG.taxRate,
                };
            } else {
                return {
                    ...DEFAULT_COUNTRY_CODE_CONFIG,
                    defaultCode: DEFAULT_COUNTRY_CODE_CONFIG.code
                };
            }
        }

        const selectedCountryData = countries.find((country: any) => (
            country.code.toUpperCase() === (accessedCountry.toUpperCase() ?? DEFAULT_COUNTRY_CODE_CONFIG.code.toUpperCase())
        ));

        return {
            defaultCode: selectedCountryData?.code ?? DEFAULT_COUNTRY_CODE_CONFIG.code,
            code: selectedCountryData?.code ?? DEFAULT_COUNTRY_CODE_CONFIG.code,
            name: selectedCountryData?.name ?? DEFAULT_COUNTRY_CODE_CONFIG.name,
            taxLabel: selectedCountryData?.taxLabel ?? DEFAULT_COUNTRY_CODE_CONFIG.taxLabel,
            currency: selectedCountryData?.currency ?? DEFAULT_COUNTRY_CODE_CONFIG.currency,
            taxRate: selectedCountryData?.taxRate ?? DEFAULT_COUNTRY_CODE_CONFIG.taxRate,
        }
    }, [getDataForEachSegment]);

    // Set state after getting data
    useEffect(() => {
        if (data) {
            const selectedCountry = getDataForEachSegment("accessed-country");
            const countries = getDataForEachSegment("countries");
            const pageClassNames: PageClassName[] = [];
            if (data?.pageMenu) {
                // Set urlSegments for all pages
                data.pageMenu.forEach((page: any) => {
                    // 1st level pages & the individual pages
                    if (page.uRLSegment) {
                        pageClassNames.push({
                            urlSegment: page.uRLSegment,
                            className: page.className,
                            title: page.title,
                            link: page.linkUrl,
                        });
                    }

                    // 2nd level pages (submenu)
                    if (page?.standardPage) {
                        page.standardPage.forEach((subPage: any) => {
                            if (subPage.uRLSegment) {
                                pageClassNames.push({
                                    urlSegment: subPage.uRLSegment,
                                    className: subPage.className,
                                    title: subPage.title,
                                    link: subPage.linkUrl,
                                })
                            }
                        });
                    }
                });
            }

            setState({
                pages: data?.pageMenu,
                headerData: {
                    headerMenuAll: getDataForEachSegment("headerMenu"), // 1st level menu
                    footerMenuAll: getDataForEachSegment("footerMenu"), // Footer menu
                    menuButton: getDataForEachSegment("menuButton"), // Custom button next to Login
                    countries: countries, // Country list
                }
            });

            const globalCurrency = globalCurrencyData?.globalCurrency;

            setAppState({
                pageClassNames,
                featureFlags: featureFlagsData?.featureFlags,
                isLoadingFeatureFlags: featureFlagsLoading,
                cart: getCart(),
                countries: countries || [],
                globalCurrencyOptions: {
                    isGlobal: (retrieveValue("selected-country")?.replace(/"/g, "") ?? "") === "other",
                    currency: globalCurrency?.currency || DEFAULT_GLOBAL_CURRENCY,
                    currencySymbol: globalCurrency?.currencySymbol || DEFAULT_GLOBAL_CURRENCY_SYMBOL,
                },
            });

            // Set the selected country
            if (selectedCountry) {
                const countryData = countries.find((country: any) => (
                    country.code.toUpperCase() === (getDataForEachSegment('user-country').toUpperCase() ?? DEFAULT_COUNTRY_CODE_CONFIG.code.toUpperCase())
                ));

                setAppState({
                    countryCode: getSelectedCountryData(countryData),
                    ipCountry: {
                        code: countryData?.code ?? DEFAULT_COUNTRY_CODE_CONFIG.code,
                        name: countryData?.name ?? DEFAULT_COUNTRY_CODE_CONFIG.name,
                        taxLabel: countryData?.taxLabel ?? DEFAULT_COUNTRY_CODE_CONFIG.taxLabel,
                        currency: countryData?.currency ?? DEFAULT_COUNTRY_CODE_CONFIG.currency,
                        taxRate: countryData?.taxRate ?? DEFAULT_COUNTRY_CODE_CONFIG.taxRate,
                    },
                });
            }
        }
    }, [
        data,
        featureFlagsData?.featureFlags,
        featureFlagsLoading,
        getCart,
        getDataForEachSegment,
        getSelectedCountryData,
        setAppState,
        setState
    ]);

    // Country handler for changing country by client
    const setCountryHandler = useCallback((countryCode: string) => {
        setState((prev: any) => ({
            headerData: {
                ...prev.headerData,
            }
        }));

        const isSameSelection = appState.country?.code !== countryCode
        let isAllowed = true;

        // The cart country is also "au" when the country is "Global".
        // In that case, it should display the cart modal when the country is changed between Australia and Global.
        if (appState.cart &&
            isSameSelection && (
            appState.cart.countryCode !== countryCode ||
            (appState.cart.countryCode === "au" && appState.cart.countryCode == countryCode && appState.cart.isGlobal)
        )) {
            isAllowed = false;
            setState({
                showConfirmationCartModal: true
            });
        }

        if (isSameSelection && isAllowed) {
            const isGlobal = countryCode === "other";

            setAppState((prev: {
                globalCurrencyOptions: GlobalCurrencyOptions,
                countryCode: {
                    defaultCode: string,
                    code: string,
                }
            }) => ({
                globalCurrencyOptions: {
                    ...prev.globalCurrencyOptions,
                    isGlobal,
                },
                countryCode: {
                    defaultCode: prev.countryCode.defaultCode,
                    code: countryCode,
                }
            }));

            persist('selected-country', countryCode);
        }

    }, [setState, appState.country?.code, appState.cart, setAppState]);

    const clearMenu = () => {
        setState({
            isClick: false,
            showBurgerMenu: false,
            isBurgerMenuClick: false,
        });
        clearCurrentPageName();
    };

    const getSubMenu = (headerMenu: StandardPage) => {
        return headerMenu ? state.pages?.find((p: StandardMenu) => p.segment === headerMenu.uRLSegment) : null;
    };

    // Get page data by URLSegment
    const getPageBySegment = useCallback((selectedSegment: string = '') => {
        const segmentsOfLocation = getURLSegments(location.pathname) ?? [];
        const defaultSegment = selectedSegment || segmentsOfLocation?.[0];
        const segmentIndex = urlSegments.indexOf(defaultSegment);
        const segment = segmentIndex === 0 ? 'headerMenu' : segmentsOfLocation[segmentIndex - 1];

        const parentPage = state.pages?.find((page: StandardMenu) => page.segment === segment); // Parent page
        return parentPage?.standardPage?.find((page: StandardPage) => page.uRLSegment === defaultSegment); // Current(Child) page
    }, [location.pathname, state.pages, urlSegments]);

    // Get breadcrumb link information by URLSegment
    const getLinkBySegment = useCallback((selectedSegment: string) => {
        const segmentIndex = urlSegments.indexOf(selectedSegment);
        const currentPage = getPageBySegment(selectedSegment);

        let link = '';
        for (let i = 0; i <= segmentIndex; i++) {
            link += '/' + urlSegments[i];
        }

        const currentPageName = appState.currentPageName;

        return {
            label: currentPage?.title
                ?? (currentURLSegment === selectedSegment && isEmpty(currentPage?.title) && currentPageName),
            link: link
        };
    }, [urlSegments, getPageBySegment, appState.currentPageName, currentURLSegment]);

    // Set the 1st level menu when browser is reloaded
    useEffect(() => {
        if (state.pages) {
            const segmentsOfLocation = getURLSegments(location.pathname);
            const defaultSegment = segmentsOfLocation[0];
            const segmentIndex = segmentsOfLocation.indexOf(defaultSegment);
            const segment = segmentIndex === 0 ? 'headerMenu' : segmentsOfLocation[segmentIndex - 1];

            const parentPage = state.pages?.find((page: StandardMenu) => page.segment === segment);
            const currentPage = parentPage?.standardPage?.find((page: StandardPage) => page.uRLSegment === defaultSegment);

            setState({
                selectedHeaderMenu: currentPage
            });
        }
    }, [
        state.pages,
        location.pathname,
        setState,
    ]);

    // Set custom menu button
    useEffect(() => {
        setState({ menuButton: state.headerData?.menuButton });
    }, [state.headerData?.menuButton, setState]);

    // Set the value that breadcrumb will be displayed or not
    useEffect(() => {
        if (currentURLSegment) {
            breadcrumbHandler(showBreadcrumb);
        }
    }, [breadcrumbHandler, showBreadcrumb, currentURLSegment]);

    const clearCurrentPageName = () => {
        setAppState({ currentPageName: '' });
    };

    const mouseOverHandler = () => {
        setState({ isClick: false });
    };

    const mouseClickHandler = () => {
        setState({ isClick: true });
        clearCurrentPageName();
    };

    // [Burger] Show and hide sub-menu
    const toggleBurgerMenu = ({ forcedClose }: any) => {
        setState((prev: DefaultState) => ({ showBurgerMenu: forcedClose ? !forcedClose : !prev.showBurgerMenu }))
    };

    // [Burger] Get sub-menu when user click the header menu
    const setSubBurgerMenu = ({ headerMenu, keepHeader }: any) => {
        setState((prev: DefaultState) => ({
            selectedHeaderMenu: keepHeader
                ? headerMenu
                : (prev.selectedHeaderMenu?.uRLSegment === headerMenu.uRLSegment ? null : headerMenu),
            isBurgerMenuClick: true,
        }));
    };

    const onMoveToHome = () => {
        navigate("/");
        toggleBurgerMenu({ forcedClose: true });
        clearCurrentPageName();
    };

    // Set footer
    useEffect(() => {
        if (state.headerData?.footerMenuAll) {
            footerMenuHandler(getFooterMenu(state.headerData.footerMenuAll));
        }
    }, [state.headerData?.footerMenuAll, footerMenuHandler]);

    const closeCountryCheckoutPopup = useCallback(() => {
        setState({ showConfirmationCartModal: false });
    }, [setState]);

    const onCart = useCallback(() => {
        closeCountryCheckoutPopup();
        navigate('/cart-summary')
    }, [closeCountryCheckoutPopup, navigate]);

    const bodyOverflowHidden = css`
        body {
            position: fixed;
            overflow: hidden;
        }
    `;

    const country = retrieveValue('selected-country')?.replace(/"/g, "") || appState.countryCode?.code.toLowerCase();
    return (
        <SectionContainer>
            {isUp(breakpoint, "xl") && (
                <NavContainer>
                    <NavShadowBox>
                        <NavWrapper>
                            <NavSide marginSide={"left"}>
                                <NavLogo onMoveToHome={onMoveToHome} />
                            </NavSide>
                            <NavLinkContainer
                                headerMenuAll={state.headerData?.headerMenuAll}
                                selectedSegments={urlSegments}
                                getSubMenu={getSubMenu}
                                mouseOverHandler={mouseOverHandler}
                                mouseClickHandler={mouseClickHandler}
                                showBreadcrumb={showBreadcrumb}
                                isClick={state.isClick}
                            />
                            <NavSide marginSide={"right"}>
                                <NavLinkButtons
                                    menuButton={state.headerData?.menuButton}
                                    closeBurgerMenu={toggleBurgerMenu}
                                    isDisabledCartIcon={isDisabledCartIcon}
                                />
                            </NavSide>
                        </NavWrapper>

                        {showBreadcrumb && (
                            <BreadcrumbContainer
                                currentURLSegment={currentURLSegment}
                                selectedSegments={urlSegments}
                                getLinkBySegment={getLinkBySegment}
                                countries={state.headerData?.countries}
                                selectedCountry={country}
                                setCountryHandler={setCountryHandler}
                                hideHeaderCountry={hideHeaderCountry}
                            />
                        )}
                    </NavShadowBox>
                </NavContainer>
            )}
            {isDown(breakpoint, "lg") && (
                <BurgerNavContainer closeBurgerMenu={toggleBurgerMenu}>
                    <BurgerNavHeaderContainer>
                        {/* Country code area for the xs (360px) */}
                        {!hideHeaderCountry && (
                            <BurgerNavSmallCountryDropdownContainer>
                                <NavCountryDropdown
                                    countries={state.headerData?.countries}
                                    selectedCountry={country}
                                    setCountryHandler={setCountryHandler}
                                />
                            </BurgerNavSmallCountryDropdownContainer>
                        )}

                        <BurgerNavWrapper>
                            <BurgerNavMenuIcon showBurgerMenu={state.showBurgerMenu} toggleBurgerMenu={toggleBurgerMenu} />
                            <NavLogo onMoveToHome={onMoveToHome} />
                            <BurgerNavSmallRightButtonsContainer>
                                <BurgerNavSmallCartWrapper>
                                    <CartIcon
                                        closeBurgerMenu={toggleBurgerMenu}
                                        isDisabledCartIcon={isDisabledCartIcon}
                                    />
                                </BurgerNavSmallCartWrapper>
                                {isOnly(breakpoint, "lg") && (
                                    <NavSide marginSide={"right"}>
                                        <NavLinkButtons
                                            menuButton={state.headerData?.menuButton}
                                            closeBurgerMenu={toggleBurgerMenu}
                                            isDisabledCartIcon={isDisabledCartIcon}
                                            hideCart
                                        />
                                    </NavSide>
                                )}
                            </BurgerNavSmallRightButtonsContainer>
                        </BurgerNavWrapper>

                        {/* BreadcrumbContainer includes breadcrumb & country dropdown */}
                        {showBreadcrumb && (
                            <BreadcrumbContainer
                                currentURLSegment={currentURLSegment}
                                selectedSegments={urlSegments}
                                getLinkBySegment={getLinkBySegment}
                                countries={state.headerData?.countries}
                                selectedCountry={country}
                                setCountryHandler={setCountryHandler}
                                hideHeaderCountry={hideHeaderCountry}
                            />
                        )}
                    </BurgerNavHeaderContainer>

                    <BurgerNavMainContainer showBurgerMenu={state.showBurgerMenu}>
                        {/* Menu type 2: Between 1024px and 1280px */}
                        {state.showBurgerMenu && (
                            <Global styles={bodyOverflowHidden} />
                        )}
                        <BurgerNavLeft>
                            {state.headerData?.headerMenuAll && state.headerData.headerMenuAll.map((headerMenu: StandardPage) => (
                                headerMenu.showInMenus ? (
                                    <BurgerNavHeaderMenuBox
                                        key={"burgerHeader" + headerMenu.id}
                                        headerMenu={headerMenu}
                                        currentURLSegment={currentURLSegment}
                                        selectedHeaderMenu={state.selectedHeaderMenu}
                                        selectedSegments={urlSegments}
                                        getSubMenu={getSubMenu}
                                        setSubMenu={setSubBurgerMenu}
                                        closeMenuHandler={clearMenu}
                                        isBurgerMenuClick={state.isBurgerMenuClick}
                                    />
                                ) : null
                            ))}
                        </BurgerNavLeft>
                        <BurgerNavRight
                            headerMenu={state.selectedHeaderMenu}
                            currentURLSegment={currentURLSegment}
                            selectedSegments={urlSegments}
                            getSubMenu={getSubMenu}
                            closeMenuHandler={clearMenu}
                        />

                        {/* Menu type 3: Less 1024px */}
                        <BurgerNavSmallContainer
                            headerMenuAll={state.headerData?.headerMenuAll}
                            menuButton={state.headerData?.menuButton}
                            selectedHeaderMenu={state.selectedHeaderMenu}
                            currentURLSegment={currentURLSegment}
                            selectedSegments={urlSegments}
                            getSubMenu={getSubMenu}
                            setSubMenu={setSubBurgerMenu}
                            closeMenuHandler={clearMenu}
                        />
                    </BurgerNavMainContainer>
                </BurgerNavContainer>
            )}
            {state.showConfirmationCartModal && (
                <ConfirmationModal
                    title={"Hold up!"}
                    message={
                        <>
                            <div>
                                You have items in your cart.
                            </div>
                            <br/>
                            <div>
                                Changing your country may affect product availability&mdash;complete your purchase now to
                                secure your items.
                            </div>
                        </>
                    }
                    confirmLabel={"Go to cart"}
                    onCancel={closeCountryCheckoutPopup}
                    onConfirm={onCart}
                />
            )}
        </SectionContainer>
    );
};

export default memo(Header);
